import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Button, IconButton } from '@mui/material';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import KeyboardTabOutlinedIcon from '@mui/icons-material/KeyboardTabOutlined';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
    pad: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '20px'
    },
    buttons: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '24px',
    },
    button: {
        width: '72px',
        height: '72px',
        background: '#EDEFF2',
        borderRadius: '48px',
        border: 'none',
        margin: '8px',
        fontSize: '28px',
        fontWeight: '400',
        lineHeight: '41px',
        letterSpacing: '0.25px',
        color: '#000',
        '&:hover': {
            background: '#d0d4d9'
        }
    },
    keyBoardButton: {
        width: '72px',
        height: '72px',
        background: '#0675DD',
        color: '#FFF',
        borderRadius: '48px',
        border: 'none',
        margin: '8px',
        '&:hover': {
            background: '#055cad'
        }
    },
    message: {
        display: 'flex',
        marginTop: '20px',
        textAlign: 'center',
        '&.invalidPin': {
            color: theme.custom.errorColor,
            fontWeight: '500',
        }
    },
    pin: {
        display: 'flex',
        marginTop: '16px',
        textAlign: 'center',
        fontSize: '40px',
        fontWeight: '500',
        lineHeight: '48px',
        letterSpacing: '35px',
        minHeight: '48px',
        paddingLeft: '33px'
    }
}));

export interface INumericKeypad {
    handleEnter: (pin: string) => void;
    validMessage: string;
    tooShortMessage: string;
    valid: boolean;

}

const NumericKeypad: React.FC<INumericKeypad> = (props) => {

    const classes = useStyles();
    const pinPlaceHolder = '----';
    const [pin, setPin] = useState(pinPlaceHolder);
    const [message, setMessage] = useState(props.validMessage);
    const [invalidPin, setInvalidPin] = useState(false);

    const handleKeyClick = (val: number) => {

        const cleanPin = pin.replace(pinPlaceHolder, '');

        if (cleanPin.length >= 4)
            return;

        setPin((pin) => `${cleanPin}${val}`);
    }

    const handleBackspaceClick = () => {
        setPin((pin) => pin.slice(0, pin.length - 1));
    }

    const handleSavePinClick = () => {
        if (pin.length < 4) {
            setMessage(props.tooShortMessage);
            setInvalidPin(true);
            
        } else {
            setMessage(props.validMessage);
            setInvalidPin(false);
            props.handleEnter(pin);
            setPin(pinPlaceHolder);
        }

        
    }

    useEffect(() => {

        const handleKeyPress = (event: KeyboardEvent) => {

            const cleanPin = pin.replace(pinPlaceHolder, '');

            const val = event.code.replace('Numpad', '').replace('Digit', '');
            const keyList = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

            if (keyList.indexOf(val) !== -1 && cleanPin.length < 4)
                setPin((pin) => `${cleanPin}${val}`);

            if (val === 'Backspace')
                handleBackspaceClick();

            if (val === 'Enter')
                handleSavePinClick();

        }

        if (!pin)
            return;

        document.addEventListener('keydown', handleKeyPress);

        return () => document.removeEventListener('keydown', handleKeyPress);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin])

    useEffect(() => {
        setMessage(props.validMessage);
    }, [props.validMessage])


    return (
        <div className={classes.pad}>
            <div className={classNames(classes.message, { invalidPin: invalidPin || !props.valid })}>{message}</div>
            <div className={classNames(classes.message, { invalidPin: invalidPin || !props.valid })}></div>
            <div className={classes.pin}>{pin}</div>
            <div className={classes.buttons}>
                <div>
                    <Button className={classes.button} onClick={() => handleKeyClick(1)}>1</Button>
                    <Button className={classes.button} onClick={() => handleKeyClick(2)}>2</Button>
                    <Button className={classes.button} onClick={() => handleKeyClick(3)}>3</Button>
                </div>
                <div>
                    <Button className={classes.button} onClick={() => handleKeyClick(4)}>4</Button>
                    <Button className={classes.button} onClick={() => handleKeyClick(5)}>5</Button>
                    <Button className={classes.button} onClick={() => handleKeyClick(6)}>6</Button>
                </div>
                <div>
                    <Button className={classes.button} onClick={() => handleKeyClick(7)}>7</Button>
                    <Button className={classes.button} onClick={() => handleKeyClick(8)}>8</Button>
                    <Button className={classes.button} onClick={() => handleKeyClick(9)}>9</Button>
                </div>
                <div>
                    <IconButton aria-label="backspace" className={classes.keyBoardButton} onClick={() => handleBackspaceClick()}>
                        <BackspaceOutlinedIcon />
                    </IconButton>
                    <Button className={classes.button} onClick={() => handleKeyClick(0)}>0</Button>
                    <IconButton aria-label="tab" className={classes.keyBoardButton} onClick={() => handleSavePinClick()}>
                        <KeyboardTabOutlinedIcon />
                    </IconButton>
                </div>
            </div>
        </div>
    );
}

export default NumericKeypad;